/**
 * batchStore — Runtime state for a batch execution of the current workflow.
 *
 * A batch runs a SUBSET of the workflow (from a user-picked start node to
 * a user-picked end node) N times, once per source. State per item is
 * isolated so nothing bleeds across items.
 *
 * Persistence: only ephemeral in-memory. Each `POST /api/executions` is
 * tagged with `metadata.batch_id` + `metadata.batch_item_index` so the
 * history can be reconstructed server-side.
 */
import { create } from 'zustand';
import { useWorkflowStore } from './workflowStore';
import { isProcessNodeType, resultTypeForNode } from '../utils/nodeResultType';
import { useCreditsStore } from './creditsStore';
import { priceParams } from '../utils/priceParams';

// ────────────────────────────────────────────────────────
// Flow validation — computes the involved subgraph between
// startNodeId and endNodeId (both directions must reach).
// ────────────────────────────────────────────────────────
function computeFlow({ nodes, edges, startId, endId }) {
  if (!startId || !endId) {
    return { valid: false, error: 'Marcá los nodos INICIO y FIN.', involvedNodeIds: [], processQueue: [] };
  }
  const startNode = nodes.find((n) => n.id === startId);
  const endNode = nodes.find((n) => n.id === endId);
  if (!startNode) return { valid: false, error: 'El nodo INICIO ya no existe.', involvedNodeIds: [], processQueue: [] };
  if (!endNode) return { valid: false, error: 'El nodo FIN ya no existe.', involvedNodeIds: [], processQueue: [] };
  if (startNode.type !== 'uploadImage') {
    return { valid: false, error: 'INICIO debe ser un nodo Upload Image (MVP).', involvedNodeIds: [], processQueue: [] };
  }
  if (!isProcessNodeType(endNode.type)) {
    return { valid: false, error: 'FIN debe ser un nodo de procesamiento.', involvedNodeIds: [], processQueue: [] };
  }
  if (startId === endId) {
    return { valid: false, error: 'INICIO y FIN no pueden ser el mismo nodo.', involvedNodeIds: [], processQueue: [] };
  }

  // Forward reachability from start
  const outAdj = new Map();
  const inAdj = new Map();
  for (const n of nodes) { outAdj.set(n.id, []); inAdj.set(n.id, []); }
  for (const e of edges) {
    outAdj.get(e.source)?.push(e.target);
    inAdj.get(e.target)?.push(e.source);
  }

  const forward = new Set([startId]);
  const fq = [startId];
  while (fq.length) {
    const cur = fq.shift();
    for (const nx of outAdj.get(cur) || []) {
      if (!forward.has(nx)) { forward.add(nx); fq.push(nx); }
    }
  }
  if (!forward.has(endId)) {
    return { valid: false, error: 'INICIO y FIN no están conectados.', involvedNodeIds: [], processQueue: [] };
  }

  // Backward reachability from end
  const backward = new Set([endId]);
  const bq = [endId];
  while (bq.length) {
    const cur = bq.shift();
    for (const nx of inAdj.get(cur) || []) {
      if (!backward.has(nx)) { backward.add(nx); bq.push(nx); }
    }
  }

  // Involved = nodes on ANY path from start to end
  const involved = [...forward].filter((id) => backward.has(id));

  // Topologically sort the involved subgraph (Kahn) so the runner
  // executes nodes in dependency order.
  const involvedSet = new Set(involved);
  const inDeg = new Map();
  for (const id of involved) {
    inDeg.set(id, (inAdj.get(id) || []).filter((s) => involvedSet.has(s)).length);
  }
  const order = [];
  const queue = involved.filter((id) => inDeg.get(id) === 0);
  while (queue.length) {
    const cur = queue.shift();
    order.push(cur);
    for (const nx of (outAdj.get(cur) || []).filter((t) => involvedSet.has(t))) {
      inDeg.set(nx, inDeg.get(nx) - 1);
      if (inDeg.get(nx) === 0) queue.push(nx);
    }
  }
  if (order.length !== involved.length) {
    return { valid: false, error: 'El subgrafo del lote contiene un ciclo.', involvedNodeIds: [], processQueue: [] };
  }

  const processQueue = order.filter((id) => {
    const n = nodes.find((nd) => nd.id === id);
    return isProcessNodeType(n?.type);
  });
  if (processQueue.length === 0) {
    return { valid: false, error: 'No hay nodos procesables entre INICIO y FIN.', involvedNodeIds: [], processQueue: [] };
  }

  return {
    valid: true,
    error: null,
    involvedNodeIds: order,
    processQueue,
  };
}

// ────────────────────────────────────────────────────────
// Store
// ────────────────────────────────────────────────────────
export const useBatchStore = create((set, get) => ({
  // Configuration
  batchMode: false,
  startNodeId: null,      // must be uploadImage
  endNodeId: null,        // any process node reachable from start
  pickingMode: null,      // 'start' | 'end' | null — canvas click captures the next node

  // Computed flow (auto-updated by recomputeFlow)
  involvedNodeIds: [],
  processQueue: [],       // subset of involvedNodeIds that will run
  flowValid: false,
  flowError: null,

  // Sources
  sources: [],            // [{ url, name, r2Key? }]

  // Runtime (populated when a batch runs)
  activeBatchId: null,
  items: [],
  currentIndex: -1,
  currentNodeId: null,
  status: 'idle',         // idle | running | completed | failed | cancelled
  error: null,
  startedAt: null,
  finishedAt: null,

  // Set on every POST /api/executions during a run for trace metadata
  activeContext: null,

  // ── Config actions ──────────────────────────────────
  setBatchMode: (on) => set({ batchMode: !!on }),
  setPickingMode: (mode) => set({ pickingMode: mode }),

  setStartNodeId: (id) => {
    set({ startNodeId: id, pickingMode: null });
    get().recomputeFlow();
  },
  setEndNodeId: (id) => {
    set({ endNodeId: id, pickingMode: null });
    get().recomputeFlow();
  },
  clearFlow: () => {
    set({ startNodeId: null, endNodeId: null, involvedNodeIds: [], processQueue: [], flowValid: false, flowError: null });
  },

  recomputeFlow: () => {
    const { startNodeId, endNodeId } = get();
    const ws = useWorkflowStore.getState();
    const result = computeFlow({ nodes: ws.nodes, edges: ws.edges, startId: startNodeId, endId: endNodeId });
    set({
      involvedNodeIds: result.involvedNodeIds,
      processQueue: result.processQueue,
      flowValid: result.valid,
      flowError: result.error,
    });
    return result;
  },

  // ── Sources ─────────────────────────────────────────
  addSource: (source) => set((s) => ({ sources: [...s.sources, source] })),
  addSources: (list) => set((s) => ({ sources: [...s.sources, ...list] })),
  removeSource: (index) => set((s) => ({ sources: s.sources.filter((_, i) => i !== index) })),
  clearSources: () => set({ sources: [] }),

  // ── Cost estimation ─────────────────────────────────
  getEstimatedCost: async () => {
    const { processQueue, sources } = get();
    const ws = useWorkflowStore.getState();
    const cs = useCreditsStore.getState();
    let perItem = 0;
    const breakdown = [];
    for (const nodeId of processQueue) {
      const n = ws.nodes.find((x) => x.id === nodeId);
      const rt = resultTypeForNode(n?.type);
      if (!rt) continue;
      // Envío 4: con el precio dinámico prendido, la estimación del lote sale
      // de /credits/quote con los params del nodo (modo "max" = la reserva).
      // Con el flag apagado el endpoint devuelve el fijo: mismo número de hoy.
      const q = await cs.fetchQuote(rt, priceParams(n?.data));
      const cost = (q && typeof q.credits === 'number') ? q.credits : cs.getCostForType(rt);
      perItem += cost;
      breakdown.push({ nodeId, nodeType: n.type, resultType: rt, cost });
    }
    return {
      perItem,
      itemCount: sources.length,
      total: perItem * sources.length,
      executionCount: processQueue.length * sources.length,
      breakdown,
    };
  },

  // ── Runtime lifecycle (called by useBatchRunner) ────
  beginBatch: ({ batchId, items }) => set({
    activeBatchId: batchId,
    items,
    currentIndex: -1,
    currentNodeId: null,
    status: 'running',
    error: null,
    startedAt: new Date().toISOString(),
    finishedAt: null,
  }),

  setActiveContext: (ctx) => set({ activeContext: ctx }),
  setCurrentIndex: (i) => set({ currentIndex: i }),
  setCurrentNodeId: (id) => set({ currentNodeId: id }),

  updateItem: (index, patch) => set((s) => ({
    items: s.items.map((it, i) => (i === index ? { ...it, ...patch } : it)),
  })),

  endBatch: ({ status, error = null }) => set({
    status,
    error,
    activeContext: null,
    currentNodeId: null,
    finishedAt: new Date().toISOString(),
  }),

  cancelBatch: () => set({
    status: 'cancelled',
    activeContext: null,
    finishedAt: new Date().toISOString(),
  }),

  // Reset runtime (preserves flow config)
  resetRuntime: () => set({
    activeBatchId: null,
    items: [],
    currentIndex: -1,
    currentNodeId: null,
    status: 'idle',
    error: null,
    activeContext: null,
    startedAt: null,
    finishedAt: null,
  }),
}));

export default useBatchStore;
